import { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useThemeStore } from './stores/themeStore';
import { useSettingsStore } from './stores/settingsStore';
import { usePortfolioStore } from './stores/portfolioStore';
import { useWindowPosition } from './hooks/useWindowPosition';
import { AssetList } from './components/AssetList';
import { Settings } from './components/Settings';
import { PortfolioOverview } from './components/PortfolioOverview';
import { Button } from './components/Button';
import { CloseButton } from './components/CloseButton';
import { OfflineIndicator } from './components/OfflineIndicator';
import { usePriceAlerts } from './hooks/usePriceAlerts';
import { useAppVisibility } from './hooks/useAppVisibility';
import { twelvedataService } from './services/twelvedata';
import { providerRegistry } from './services/providerRegistry';
import { unifiedApiService } from './services/unifiedApi';
import { OfflineProvider } from './contexts/OfflineContext';
import { assetDataService } from './services/assetDataService';
import { assetDictionaryCache, type DictMeta } from './services/assetDictionaryCache';
import './styles/index.css';
import './i18n';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

type View = 'portfolio' | 'assets' | 'settings';

function App() {
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  const { twelvedataApiKey, apiKeys } = useSettingsStore();
  const { portfolio } = usePortfolioStore();
  const [currentView, setCurrentView] = useState<View>('assets');

  useWindowPosition();
  usePriceAlerts();
  const isAppVisible = useAppVisibility();

  // remember.md: exe 关闭/托盘 或 浏览器离开页面 后不再请求网络
  useEffect(() => {
    if (!isAppVisible) {
      providerRegistry.stopHealthChecks();
      unifiedApiService.cancelRequests();
    } else {
      providerRegistry.startHealthChecks();
    }
  }, [isAppVisible]);

  useEffect(() => {
    const onPageHide = () => {
      providerRegistry.stopHealthChecks();
      unifiedApiService.cancelRequests();
    };
    document.addEventListener('pagehide', onPageHide);
    return () => document.removeEventListener('pagehide', onPageHide);
  }, []);

  // Initialize AssetDataService on app startup
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await assetDataService.initialize();
      } catch (error) {
        console.error('Failed to initialize AssetDataService:', error);
      }

      // 1) Load cached full dictionaries (HK/A) for offline search
      // Priority: Built-in > localStorage (both are fast, built-in is fastest)
      try {
        const { meta, assets, source } = await assetDictionaryCache.loadCache();
        if (!cancelled && assets.length > 0) {
          await assetDataService.mergeAndUpdateAssets(assets);
          console.log(
            `[AssetDict] loaded ${source} hk/a count=${assets.length} fetchedAt=${meta?.fetchedAt || 'unknown'}`
          );
        }
      } catch (error) {
        console.warn('[AssetDict] failed to load cache:', error);
      }

      // 2) Refresh in background when cache is missing or stale
      // Only refresh if:
      // - No data available (source === 'none')
      // - localStorage cache exists but is stale
      // - Built-in data exists but localStorage is missing/stale (to sync localStorage)
      try {
        const { meta, source } = await assetDictionaryCache.loadCache();
        if (cancelled) return;
        
        // Check if we need to refresh
        let shouldRefreshNow = false;
        
        if (source === 'none') {
          // No data at all, need to fetch
          shouldRefreshNow = true;
        } else if (source === 'localStorage') {
          // Using localStorage, check if stale
          shouldRefreshNow = assetDictionaryCache.shouldRefresh(meta);
        } else if (source === 'builtin') {
          // Using built-in, check if localStorage needs sync
          // Only refresh if localStorage is missing or stale (but don't force refresh if built-in is recent)
          try {
            if (typeof window !== 'undefined' && window.localStorage) {
              const lsMetaJson = window.localStorage.getItem('assetDict.meta.v1');
              if (lsMetaJson) {
                try {
                  const lsMeta = JSON.parse(lsMetaJson) as DictMeta;
                  if (assetDictionaryCache.shouldRefresh(lsMeta)) {
                    // localStorage exists but is stale, refresh in background to sync
                    shouldRefreshNow = true;
                  }
                } catch {
                  // Invalid meta, will refresh
                  shouldRefreshNow = true;
                }
              }
              // If no localStorage meta, don't force refresh (built-in is sufficient)
            }
          } catch {
            // localStorage not available, skip
          }
        }
        
        if (shouldRefreshNow) {
          // Use a flag to prevent multiple concurrent refreshes
          if (!(window as any).__dictRefreshInProgress) {
            (window as any).__dictRefreshInProgress = true;
            assetDictionaryCache
              .refreshFromAktoolsAndPersist()
              .then(async ({ assets }) => {
                if (cancelled) return;
                await assetDataService.mergeAndUpdateAssets(assets);
                console.log(`[AssetDict] refreshed from AKTools hk/a count=${assets.length}`);
              })
              .catch((e) => {
                console.warn('[AssetDict] refresh failed:', e);
              })
              .finally(() => {
                (window as any).__dictRefreshInProgress = false;
              });
          }
        }
      } catch (error) {
        console.warn('[AssetDict] refresh check failed:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // 添加 F12 快捷键打开开发者工具（作为后备方案）, default.json "core:webview:deny-internal-toggle-devtools" or "core:webview:allow-internal-toggle-devtools"
  // useEffect(() => {
  //   const handleKeyDown = async (e: KeyboardEvent) => {
  //     if (e.key === 'F12') {
  //       e.preventDefault();
  //       try {
  //         const { safeInvoke } = await import('@/utils/tauri');
  //         await safeInvoke('toggle_devtools');
  //       } catch (error) {
  //         console.error('Failed to toggle devtools:', error);
  //       }
  //     }
  //   };

  //   window.addEventListener('keydown', handleKeyDown);
  //   return () => {
  //     window.removeEventListener('keydown', handleKeyDown);
  //   };
  // }, []);

  useEffect(() => {
    // 应用主题
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  useEffect(() => {
    // 从设置存储加载API密钥到服务
    if (apiKeys.twelvedata) {
      twelvedataService.setApiKey(apiKeys.twelvedata);
    } else if (twelvedataApiKey) {
      // 向后兼容：如果 apiKeys 中没有，使用旧的 twelvedataApiKey
      twelvedataService.setApiKey(twelvedataApiKey);
    } else {
      // 如果没有配置，尝试从环境变量加载（空字符串视为未配置）
      const envKey = import.meta.env.VITE_TWELVEDATA_API_KEY;
      if (envKey && envKey.trim() !== '') {
        twelvedataService.setApiKey(envKey);
      }
    }
  }, [twelvedataApiKey, apiKeys.twelvedata]);

  return (
    <QueryClientProvider client={queryClient}>
      <OfflineProvider>
        <OfflineIndicator />
        <AnimatePresence mode="wait">
        <motion.div
          key={currentView}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
          className="w-full h-screen bg-macos-bg dark:bg-macos-bg-dark backdrop-blur-macos font-macos relative"
          style={{ borderRadius: '24px', minHeight: '100vh', border: 'none', outline: 'none', boxShadow: 'none' }}
        >
          {/* 关闭按钮 - 绝对定位在右上角 */}
          <CloseButton />
          
          <div className="p-4 h-full flex flex-col">
            {/* 标题栏 - 第一行 */}
            <div className="flex items-center justify-between mb-2 flex-shrink-0">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {t('app.dashboard')}
              </h1>
              {/* 占位符，保持标题居左，关闭按钮在绝对定位 */}
              <div className="w-8"></div>
            </div>

            {/* 导航栏 - 第二行 */}
            <div className="flex items-end justify-between gap-2 mb-4 flex-shrink-0">
              <div className="flex gap-2">
                <Button
                  variant={currentView === 'assets' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setCurrentView('assets')}
                >
                  {t('nav.assets')}
                </Button>
                <Button
                  variant={currentView === 'portfolio' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setCurrentView('portfolio')}
                >
                  {t('nav.portfolio')}
                </Button>
                <Button
                  variant={currentView === 'settings' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setCurrentView('settings')}
                >
                  {t('nav.settings')}
                </Button>
              </div>
            </div>

            {/* 内容区域 - 可滚动 */}
            <div className="flex-1 overflow-y-auto scrollbar-hide">
              <AnimatePresence mode="wait">
                {currentView === 'portfolio' && (
                  <motion.div
                    key="portfolio"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <PortfolioOverview />
                  </motion.div>
                )}
                {currentView === 'assets' && (
                  <motion.div
                    key="assets"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <AssetList />
                  </motion.div>
                )}
                {currentView === 'settings' && (
                  <motion.div
                    key="settings"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Settings />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
      </OfflineProvider>
    </QueryClientProvider>
  );
}

export default App;
